import { graphql, Link, useStaticQuery } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import Close from '@mui/icons-material/Close';
import Menu from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Chip,
  Container, IconButton,
  Button as MuiButton,
  Stack, Theme, Toolbar
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  useContentfulInspectorMode, useContentfulLiveUpdates
} from '../shared/hooks/contentful';

import { useClientOnlyCart as useCart } from '../shared/shopify/hooks/useClientOnlyCart';

import { ConvergeSdk, PersonIdentifiers, TrackingActivityNames } from '@seeka-labs/converge';
import Cookies from 'js-cookie';
import { useAccountNavItems } from '../shared/hooks/useAccountNavItems';
import { useAutomaticDiscountList } from '../shared/hooks/useAutomaticDiscountList';
import useLoggedInCustomer from '../shared/hooks/useLoggedInCustomer';
import { notPrintable } from '../shared/styles/notPrintable';
import { AccountNavButton } from './account/accountNavButton';

import { klaviyoFormIds } from '../shared/klaviyo/constants';
import { CartButton } from './cart/cartButton';
import { NavItemGroupNavItem } from './nav-item-group';
import { SiteBanner } from './site-banner';
import { Button, ContentfulAssetImage, NavLink } from './ui';

export const mobileNavLinkStyle = (muiTheme: Theme) => ({
  display: "block",
  color: "white",
  zIndex: 1,
  textAlign: 'left',
  fontSize: muiTheme.typography.h3.fontSize,
  paddingTop: muiTheme.spacing(1),
  paddingBottom: muiTheme.spacing(1),
  paddingLeft: muiTheme.spacing(4),
  paddingRight: muiTheme.spacing(4),
  marginBottom: muiTheme.spacing(1),
})

export const mobileAppFloatingMenuStyle = (muiTheme: Theme) => ({
  color: "white",
  zIndex: 999,
  fontSize: muiTheme.typography.h3.fontSize,
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  pt: 15,
  pl: 1,
  pr: 1,
  position: "absolute",
  background: `linear-gradient(180deg, ${muiTheme.palette.gradients.dark.end} -50%, ${muiTheme.palette.gradients.dark.start} 100%)`,
})

export const desktopAppBarStyle = () => ({
  background: "transparent",
  display: {
    xs: "none",
    sm: "none",
    md: "none",
    lg: "inherit",
    xl: "inherit",
  },
  // width: "100%",
  // top: 0,
  // left: 0,
  // right: 0,
})

export const mobileAppBarStyle = () => ({
  background: "transparent",
  display: {
    xs: "inherit",
    sm: "inherit",
    md: "inherit",
    lg: "none",
    xl: "none",
  },
  // pb: 2,
  // width: "100%",
  // top: 0,
  // left: 0,
  // right: 0,
})

export type NavItem = {
  contentful_id: string
  id: string
  navItemType: "Link"
  href: string
  text: string
}

export type NavItemGroup = {
  contentful_id: string
  id: string
  navItemType: "Group"
  name: string
  navItems: NavItemGroupNavItem[]
}

export const baseLogoHeight = 48
export const baseLogoWidth = 230

export default function Header({ location, isSiteFramed }: { location: Location, isSiteFramed: boolean }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { status, totalQuantity, lines } = useCart();
  const cartQuantity = totalQuantity || 0;

  const convergeStatus = typeof window !== 'undefined' ? ((window as any).Converge as ConvergeSdk | undefined)?.status : undefined;

  useEffect(() => {
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        const converge = ((window as any).Converge as ConvergeSdk | undefined);
        if (converge && converge.handlers && converge.status && converge.status.isInitialised) {
          converge.handlers.getCart = async () => {
            return {
              products: lines.map(line => {
                // console.log('heree line', line)
                return {
                  productIdentifier: line.merchandise.product.id.split('/').splice(-1)[0],
                  productName: line.merchandise.product.title,
                  categoryName: line.merchandise.product.productType,
                  quantity: line.quantity,
                  currencyCode: line.cost.amountPerQuantity.currencyCode,
                  sku: line.merchandise.sku,
                  unitPrice: parseFloat(line.cost.amountPerQuantity.amount),
                }
              }),
            }
          }
        }

      }
    }, 3000)
  }, [convergeStatus])

  useEffect(() => {
    // console.log('heree status', status)
    if (status === 'updating' && location.pathname !== '/cart' && location.pathname !== '/cart/') {
      setTimeout(() => {
        enqueueSnackbar('Updated cart', { variant: 'success', action: <MuiButton component={Link} to="/cart" onClick={() => closeSnackbar()} size='small' sx={{ color: 'white', borderColor: 'white!important', fontWeight: 'normal' }} variant="outlined" disableElevation>View cart</MuiButton> })
      }, 1000)
    }
  }, [status])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener("converge.activity.tracked", (ev) => {
        const activityName: TrackingActivityNames = (ev as any).detail?.activityName;
        const activityNameCustom: string | undefined | null = (ev as any).detail?.props?.activityNameCustom;
        const klav = (window as any).klaviyo;

        if (!activityName || !klav || activityName === TrackingActivityNames.ViewPage) return;

        if (activityName === TrackingActivityNames.Custom && activityNameCustom) {
          klav.track(activityNameCustom);
        }
        else if (activityName === TrackingActivityNames.ViewProduct || activityName === TrackingActivityNames.AddToCart) {
          // no-op
        }
        else if (activityName === TrackingActivityNames.ViewContentItem) {
          // no-op
        }
        else {
          klav.track(activityName);
        }
      });

      window.addEventListener("converge.identity.changed", (ev) => {
        const seekaIdentity = (ev as any).detail?.identifiers as PersonIdentifiers | undefined | null;
        const seekaPId = seekaIdentity?.seekaPId;
        const klav = (window as any).klaviyo;

        const converge = ((window as any).Converge as ConvergeSdk | undefined);
        if (klav && klav.identify && seekaIdentity && seekaPId && converge) {
          const klavIdentity: any = {
            external_id: seekaPId
          };
          if (seekaIdentity.email && seekaIdentity.email.length > 0) {
            klavIdentity['email'] = seekaIdentity.email[0];
          }
          if (seekaIdentity.phone && seekaIdentity.phone.length > 0) {
            klavIdentity['phone_number'] = seekaIdentity.phone[0];
          }
          if (seekaIdentity.firstName && seekaIdentity.firstName.length > 0) {
            klavIdentity['first_name'] = seekaIdentity.firstName[0];
          }
          if (seekaIdentity.lastName && seekaIdentity.lastName.length > 0) {
            klavIdentity['last_name'] = seekaIdentity.lastName[0];
          }
          klav.identify(klavIdentity);
        }

        // const clar = (window as any).clarit
        // if (clar && seekaPId) {
        //   clar("set", "seekaProfileId", seekaPId);
        // }
      });

      // Klaviyo newsletter tracking
      let discountCodeInterval: NodeJS.Timeout;
      window.addEventListener("klaviyoForms", (e) => {
        const evData = (e as any).detail;
        if (evData && evData.type == 'submit') {
          // console.log('heree evData', evData)
          if (evData.formId === klaviyoFormIds.newsletterWithDiscount || evData.formId === klaviyoFormIds.newsletterNoDiscount) {
            // newsletter form          
            ((window as any).Converge as ConvergeSdk | undefined)?.track.newsletterSignup();

            const coupon = tryGetKlaviyoDiscountCode();
            if (coupon) {
              addKlaviyoCouponToCart(coupon);
            }
            else {
              discountCodeInterval = setInterval(() => {
                const coupon = tryGetKlaviyoDiscountCode();
                if (coupon) {
                  try {
                    addKlaviyoCouponToCart(coupon);
                  }
                  catch (err) {
                    console.error('Failed to add Klaviyo coupon to cart', err);
                  }
                  clearInterval(discountCodeInterval);
                }
              }, 1500);
            }
          }
          if (evData.formId === klaviyoFormIds.registerLeadForm) {
            // Black friday register form       
            ((window as any).Converge as ConvergeSdk | undefined)?.track.lead();
          }
          if (evData.metaData && (evData.metaData['$email'] || evData.metaData['$phone_number'])) {
            ((window as any).Converge as ConvergeSdk | undefined)?.identity.mergeProfile({
              email: evData.metaData['$email'] ? [evData.metaData['$email']] : [],
              phone: evData.metaData['$phone_number'] ? [evData.metaData['$phone_number']] : [],
            })
          }
        }
      });
    }
  }, [])

  const addKlaviyoCouponToCart = (couponCode: string) => {
    // console.log('heree couponCode added', couponCode)
    // discountCodesUpdate([...(discountCodes?.map(e => e.code) || []), couponCode].filter(Boolean));
    Cookies.set('klaviyo_coupon_code', couponCode);
  }

  const tryGetKlaviyoDiscountCode = () => {
    const couponInput = document.querySelector<HTMLButtonElement>('button.needsclick[aria-label="Copy coupon code"]')
    if (couponInput) {
      return couponInput.innerText;
    }
  }

  const headerDataQuery: Queries.HeaderComponentQuery = useStaticQuery(graphql`
    query HeaderComponent {
      layoutHeader: contentfulLayoutHeader {
        id
        contentful_id
        __typename
        desktopLogo: logo {
          id
          title      
          localFile {
            childImageSharp { 
              gatsbyImageData(
                placeholder: NONE
                quality: 100
                width: 280
                breakpoints: [150, 200, 280]
                layout: CONSTRAINED
              )
            }
          }
          contentful_id
          __typename
        }
        mobileLogo: logo {
          id
          title      
          localFile {
            childImageSharp { 
              gatsbyImageData(
                placeholder: NONE
                quality: 100
                width: 180
                breakpoints: [100, 180]
                layout: CONSTRAINED
              )
            }
          }
          contentful_id
          __typename
        }
        navItems {
          id
          contentful_id
          __typename
          ... on ContentfulNavItem {
            href
            text
          }
        }
        cta {
          id
          href
          text
          contentful_id
          __typename
        }
      }
    }
  `)

  const { activeDiscounts } = useAutomaticDiscountList();

  const headerData = useContentfulLiveUpdates({
    ...headerDataQuery.layoutHeader,
    sys: { id: headerDataQuery.layoutHeader.contentful_id },
  });

  const NotPrintable = useMemo(() => styled('div')(notPrintable), []);

  const banners = activeDiscounts.map((node) => ({ text: node.title }))

  // const hasSiteBanners = useMemo(() => banners.length > 0, [banners])
  const headerProps = useMemo(() => {
    return { ...headerDataQuery, layoutHeader: headerData }
  }, [])

  return (
    isSiteFramed ? <></> :
      <>
        <NotPrintable>
          <MobileHeader cartQuantity={cartQuantity} isSiteFramed={isSiteFramed} layoutHeader={headerProps.layoutHeader} banners={banners} />
          <DesktopHeader cartQuantity={cartQuantity} isSiteFramed={isSiteFramed} layoutHeader={headerProps.layoutHeader} banners={banners} />
        </NotPrintable>
      </>
  )
}

type HeaderComponentProps = Queries.HeaderComponentQuery & {
  cartQuantity: number
  isSiteFramed: boolean
  banners: {
    text: string;
  }[]
}

const DesktopHeader = ({ cartQuantity, layoutHeader, banners, isSiteFramed }: HeaderComponentProps) => {
  const { navItems, cta, desktopLogo } = layoutHeader
  const theme = useTheme()

  const appBarStyle = desktopAppBarStyle()
  const inspectorProps = useContentfulInspectorMode();

  const { wholesale } = useLoggedInCustomer();

  // const { isBigDefaultDiscount } = useAutomaticDiscountList();

  // const toolbarTopMargin = useMemo(() => {
  //   return hasSiteBanners || isBigDefaultDiscount ? bannerHeight(isBigDefaultDiscount, loggedInCustomer.wholesale) + 'px' : null
  // }, [])

  return (
    (
      <AppBar position="absolute" elevation={0} sx={[{
        ...appBarStyle
      }, {
        // marginTop: toolbarTopMargin
      }]}>
        <SiteBanner banners={banners} />
        <Toolbar disableGutters sx={{ pt: wholesale ? undefined : 2 }}>
          <Container>
            <Stack
              direction={"row"}
              justifyContent="space-between"
              alignItems="center"
            >
              <Link style={{ textDecoration: 'none' }} aria-label='Go to home page' to="/" id="header-logo-desktop">
                <Box sx={{ display: "none" }}>Home</Box>
                <Stack role="banner" direction="row" gap={2} alignItems="center">
                  <ContentfulAssetImage
                    // {...logo}
                    asset={desktopLogo}
                    // skipGatsbyImage
                    aboveFold
                    // imgStyle={{
                    //   width: '100%',
                    //   maxWidth: baseLogoWidth + "px",
                    //   height: baseLogoHeight + "px",
                    // }}
                    // imgStyle={{ marginTop: '-7px' }}
                    objectFit="contain"
                    contentfulPreviewProps={inspectorProps({
                      entryId: layoutHeader.contentful_id,
                      fieldId: 'logo',
                    })}
                  />
                  {wholesale ? <Chip color="secondary" style={{ backgroundColor: 'transparent' }} variant="outlined" label="Wholesale" size="small" /> : <></>}
                </Stack>
              </Link>
              <Stack
                direction={"row"}
                justifyContent="space-between"
                alignItems="center"
                gap={{ md: 2, lg: 4 }}
              >
                <nav
                  role="navigation"
                  {...inspectorProps({
                    entryId: layoutHeader.contentful_id,
                    fieldId: 'navItems',
                  })}>
                  <Stack
                    gap={{ md: 1, lg: 2 }}
                    direction={"row"}
                    justifyContent="space-evenly"
                    alignItems="center"
                  >
                    {navItems &&
                      navItems.map((navItem) => (
                        <div key={`header-desktop-nav-${navItem.id}`}>
                          <NavLink
                            id={`header-desktop-nav-${navItem.id}`}
                            to={navItem.href}
                            sx={{
                              color: theme.palette.gradients.dark.contrastText,
                            }}
                            contentfulPreviewProps={inspectorProps({
                              entryId: navItem.contentful_id,
                              fieldId: 'text',
                            })}
                          >
                            {navItem.text}
                          </NavLink>
                        </div>
                      ))}
                  </Stack>
                </nav>

                {/* <Link to="/search" className={searchButton}>
          <SearchIcon />
        </Link> */}
                {cta && (
                  <div {...inspectorProps({
                    entryId: cta.id,
                    fieldId: 'text',
                  })}>
                    <Button
                      to={cta.href}
                      variant="contained"
                      disableElevation
                      sx={
                        {
                          borderColor: "white",
                        }
                      }
                    >
                      {cta.text}
                    </Button>
                  </div>
                )}
                <Stack direction="row" gap={1} alignItems="center">
                  <CartButton quantity={cartQuantity} />
                  <AccountNavButton />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Toolbar>
      </AppBar>)
  );
}

const MobileHeader = ({ cartQuantity, layoutHeader, banners }: HeaderComponentProps) => {
  const { navItems, cta, mobileLogo } = layoutHeader
  const inspectorProps = useContentfulInspectorMode();

  const accountNavItem = useAccountNavItems();
  // const loggedInCustomer = useLoggedInCustomer();
  const allNavItems = useMemo(() => {
    return [...navItems, ...accountNavItem.navItems]
  }, [accountNavItem.navItems])

  const theme = useTheme()

  const appBarStyle = mobileAppBarStyle()
  const openMenuStyle = mobileAppFloatingMenuStyle(theme)
  const mobileLinkStyling = mobileNavLinkStyle(theme)
  const [isOpen, setOpen] = useState(false)

  // const { isBigDefaultDiscount } = useAutomaticDiscountList();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
      document.documentElement.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
      document.documentElement.style.overflow = ""
    }
  }, [isOpen])

  // const toolbarTopMargin = useMemo(() => {
  //   return bannerHeight(isBigDefaultDiscount, loggedInCustomer.wholesale)
  // }, [])

  return (
    <>
      <AppBar position="absolute" elevation={0} sx={{ ...appBarStyle, marginTop: isOpen ? 1 : undefined }}>
        {!isOpen && <SiteBanner banners={banners} />}
        <Toolbar disableGutters sx={{ p: 0, pt: isOpen ? '25px' : { xs: 0.5, sm: 1, md: 2 }, pb: isOpen ? undefined : { xs: 0.5, sm: 1, md: 2 } }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: "100%" }}
            justifyContent={"space-between"}
          >
            <Link aria-label='Go to home page' style={{ textDecoration: 'none' }} to="/" onClick={() => setOpen(false)} id="header-mobile-logo">
              <Box role="banner" px={2}>
                <Box sx={{ display: "none" }}>Home</Box>
                <Box
                  sx={{
                    // maxHeight: {
                    //   xs: baseLogoHeight * 0.75 + "px",
                    //   sm: baseLogoHeight * 0.85 + "px",
                    //   md: baseLogoHeight + "px",
                    // },
                    // width: {
                    //   xs: baseLogoWidth * 0.75 + "px",
                    //   sm: baseLogoWidth * 0.85 + "px",
                    //   md: baseLogoWidth + "px",
                    // },
                  }}
                >
                  <Box>
                    <ContentfulAssetImage
                      // {...mobileLogo}
                      aboveFold
                      asset={mobileLogo}

                      // skipGatsbyImage
                      // imgStyle={{
                      //   width: '100%',
                      //   maxWidth: baseLogoWidth + "px",
                      // }}
                      // imgStyle={{
                      //   maxHeight: {
                      //     xs: baseLogoHeight * 0.75 + "px",
                      //     sm: baseLogoHeight * 0.85 + "px",
                      //     md: baseLogoHeight + "px",
                      //   }
                      // }}
                      objectFit="contain"
                      contentfulPreviewProps={inspectorProps({
                        entryId: layoutHeader.contentful_id,
                        fieldId: 'logo',
                      })}
                    /></Box>
                </Box>
              </Box>
            </Link>

            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              role="navigation"
              justifyContent={"flex-end"}
            >
              {cta && (
                <Button
                  to={cta.href}
                  variant={isOpen ? "outlined" : "contained"}
                  sx={{
                    borderColor: "white",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    p: { xs: "7px 12px", sm: "10px 16px" },
                    visibility: isOpen ? 'hidden' : 'visible'
                    // marginRight: isOpen ? "18px" : null,
                  }}
                  {...inspectorProps({
                    entryId: layoutHeader.contentful_id,
                    fieldId: 'cta',
                  })}
                >
                  {cta.text}
                </Button>
              )}
              {/* <Link to="/search" className={searchButton}>
          <SearchIcon />
        </Link> */}

              <CartButton quantity={cartQuantity} onClick={() => setOpen(false)} />
              <div>
                <IconButton
                  title="Toggle menu"
                  onClick={() => setOpen(!isOpen)}
                >
                  {isOpen ? (
                    <Close sx={{ color: "white" }} />
                  ) : (
                    <Menu sx={{ color: "white" }} />
                  )}
                </IconButton>
              </div>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
      {isOpen && (
        <Box sx={openMenuStyle}>
          <nav>
            <Stack direction={'column'} sx={{ width: '100%' }} alignItems={'stretch'}>
              {allNavItems?.map((navItem) =>
                <NavLink
                  id={`header-mobile-nav-${navItem.id}`}
                  key={`header-mobile-nav-${navItem.id}`}
                  to={navItem.href}
                  onClick={() => {
                    setOpen(false);
                    if (navItem.onClick) navItem.onClick();
                  }}
                  sx={mobileLinkStyling}
                  contentfulPreviewProps={inspectorProps({
                    entryId: navItem.contentful_id,
                    fieldId: 'text',
                  })}
                >
                  {navItem.text}
                </NavLink>
              )}
              {cta && (
                <Box sx={{ ml: 4, mr: 4, mt: 3 }}>
                  <NavLink
                    id={`header-mobile-cta-${cta.id}`}
                    to={cta.href}
                    variant={"contained"}
                    onClick={() => setOpen(false)}
                    // style={mobileLinkStyling}
                    sx={{
                      color: "white",
                      borderColor: "white",
                      fontSize: "1rem",
                      display: 'block',
                      textAlign: 'center'
                      // p: "10px 16px",
                      // mt: "20px",
                      // ml: "20px",
                      // mr: "25px",
                      // marginRight: isOpen ? "18px" : null,
                    }}
                    contentfulPreviewProps={inspectorProps({
                      entryId: layoutHeader.contentful_id,
                      fieldId: 'cta',
                    })}
                  >
                    {cta.text}
                  </NavLink>
                </Box>
              )}
            </Stack>
          </nav>
        </Box>
      )}
    </>
  )
}
